import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { toast, Card } from '../V2Components';
import FieldErrorList from '../Electrical/FieldErrorList';

// Client-side mirror of the backend StructureEventType enum (models/enums.py).
// Values must match exactly — Pydantic does exact-value validation. The backend
// test test_structure_enum_values_match_frontend_contract pins them.
export const EVENT_TYPES = ['Installation', 'Repair', 'Inspection', 'Replacement', 'Maintenance', 'Other'];

const EMPTY_FORM = {
    event_type: 'Installation',
    event_date: '',
    cost: '',
    contractor: '',
    notes: '',
};

/**
 * Create/edit form for a StructureEvent, rendered as an inline in-page card
 * inside the structure's panel (matching CircuitForm). Returns null when closed;
 * the parent (StructuresTab) owns open/close state and the API call.
 *
 * Props:
 *   isOpen   - boolean
 *   onClose  - () => void
 *   onSave   - (payload) => Promise<void>
 *   onDelete - () => void  — parent owns the delete; only shown in edit mode
 *   event    - existing StructureEvent row for edit mode, null for create
 *   saving   - boolean
 *   fieldErrors - array of Pydantic 422 error objects ({loc, msg})
 */
const StructureEventForm = ({ isOpen, onClose, onSave, onDelete, event, saving, fieldErrors = [] }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Reset on every (re)open DURING RENDER, not in a useEffect. The component
    // stays mounted while closed (it returns null), so formData still holds the
    // last submission; an effect-based reset commits those stale values to the
    // DOM first and clears them a tick later, losing any input that lands in
    // between (see CircuitForm). Setting state while rendering makes React
    // re-render before committing, so stale values never reach the DOM.
    const openKey = isOpen ? `${event?.id ?? 'new'}` : null;
    const [prevOpenKey, setPrevOpenKey] = useState(null);
    if (openKey !== prevOpenKey) {
        setPrevOpenKey(openKey);
        if (openKey !== null) {
            if (event && event.id) {
                setFormData({
                    event_type: event.event_type || 'Installation',
                    event_date: event.event_date || '',
                    cost: event.cost ?? '',
                    contractor: event.contractor || '',
                    notes: event.notes || '',
                });
            } else {
                setFormData(EMPTY_FORM);
            }
        }
    }

    const isEdit = !!(event && event.id);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.event_date) {
            toast.error('Event date is required.');
            return;
        }
        const payload = {
            event_type: formData.event_type,
            event_date: formData.event_date,
            cost: formData.cost === '' ? null : Number(formData.cost),
            contractor: formData.contractor.trim() || null,
            notes: formData.notes.trim() || null,
        };
        onSave(payload);
    };

    if (!isOpen) return null;

    const title = isEdit ? 'Edit Event' : 'Add Event';
    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    return (
        <Card className="p-5 mb-4 bg-[var(--bg-hover)]">
            <h4 className="text-sm font-bold text-[var(--text-main)] uppercase tracking-wide mb-3">
                {title}
            </h4>
            <form onSubmit={handleSubmit} aria-label={title} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="event-type" className={labelClass}>Type</label>
                        <select
                            id="event-type"
                            value={formData.event_type}
                            onChange={(e) => handleChange('event_type', e.target.value)}
                            className={inputClass}
                        >
                            {EVENT_TYPES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="event-date" className={labelClass}>
                            Date <span className="text-[var(--error)]">*</span>
                        </label>
                        <input
                            id="event-date"
                            type="date"
                            value={formData.event_date}
                            onChange={(e) => handleChange('event_date', e.target.value)}
                            className={inputClass}
                            required
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="event-cost" className={labelClass}>Cost ($)</label>
                        <input
                            id="event-cost"
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.cost}
                            onChange={(e) => handleChange('cost', e.target.value)}
                            placeholder="0.00"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label htmlFor="event-contractor" className={labelClass}>Contractor</label>
                        <input
                            id="event-contractor"
                            value={formData.contractor}
                            onChange={(e) => handleChange('contractor', e.target.value)}
                            placeholder="Who did the work"
                            className={inputClass}
                        />
                    </div>
                </div>

                <div>
                    <label htmlFor="event-notes" className={labelClass}>Notes</label>
                    <textarea
                        id="event-notes"
                        value={formData.notes}
                        onChange={(e) => handleChange('notes', e.target.value)}
                        placeholder="What was done, materials used, warranty details..."
                        rows={2}
                        className={`${inputClass} resize-none`}
                    />
                </div>

                <FieldErrorList errors={fieldErrors} />

                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
                    </button>
                </div>

                {isEdit && onDelete && (
                    <div className="pt-2 border-t border-[var(--border)]">
                        <button
                            type="button"
                            onClick={onDelete}
                            disabled={saving}
                            className="flex items-center gap-2 text-sm text-[var(--error)] hover:opacity-80 transition disabled:opacity-50"
                        >
                            <Trash2 size={16} />
                            Delete event
                        </button>
                    </div>
                )}
            </form>
        </Card>
    );
};

export default StructureEventForm;
